import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Input } from "@vds/inputs";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { Icon } from "@vds/icons";
import { IconButton } from "@mui/material";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "@vds/modals";
import { Button, ButtonGroup } from "@vds/buttons";
import styles from "./components/basicForm.module.css";
import { FileUploader } from "react-drag-drop-files";
import { Notification } from "@vds/notifications";
import { Title } from "@vds/typography";
import { showNotificationBanner } from "../../store/actions";
import { hasPermission } from "../../global/utility/permissionUtils";
import { useDispatch } from "react-redux";
import { validateNameField } from "./sitesUtil";
import { NAMING_VALIDATION_ERROR } from "../../global/constants/errorConstants";
import { device } from "../../styles/breakpoints";

const IndividualFPFrame = styled.div`
  /* Mobile-first: full width, flexible height */
  width: 100%;
  min-height: 400px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
  border-radius: 4px;

  /* Original tablet/desktop styles (unchanged from before) */
  @media ${device.tablet} {
    width: 100%;
    max-width: 350px;
    min-height: 420px;
    flex-shrink: 0;
  }

  @media ${device.desktop} {
    max-width: 400px;
    min-height: 440px;
  }
`;

export const StyledButton = styled.button`
  text-decoration: underline;
  background-color: transparent;
  cursor: pointer;
  margin-left: -1.1rem;
  &&:hover {
    border-color: transparent; 
  }
`;

const FrameForImages = styled.div`
  /* Mobile-first: flexible dimensions */
  flex-grow: 1;
  background: url(${(props) => props.imageUrl}) no-repeat center center;
  background-size: cover;
  width: 100%;
  min-height: 200px;
  height: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;

  /* Original tablet/desktop styles (unchanged from before) */
  @media ${device.tablet} {
    min-height: 240px;
  }

  @media ${device.desktop} {
    min-height: 260px;
  }
`;

const UploadedImageNameEdit = styled.div`
  /* Mobile-first: readable text */
  font-family: Verizon-NHG-eDS;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-align: left;
  margin-top: 8px;

  @media ${device.tablet} {
    font-size: 14px;
    line-height: 18px;
    margin-top: 12px;
  }
`;

const FloorPlanHeader = styled.div`
  /* Mobile-first: flex header with responsive spacing */
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  @media ${device.tablet} {
    margin-bottom: 12px;
  }
`;

const FloorPlanLabel = styled.span`
  /* Mobile-first: responsive label text */
  font-family: Verizon-NHG-eDS;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  padding: 8px 0;

  @media ${device.tablet} {
    font-size: 16px;
    line-height: 20px;
    padding: 10px 0;
  }
`;

function FloorPlanTileEdit({
  file,
  index,
  isEditable,
  handleDelete,
  mode,
  sizeErrorFunction,
  fileTypes,
  maxSize,
  handleFileChange,
  reviewPage,
  value,
  onValidationChange,
}) {
  const cvAppRoisWithCVApps = useSelector(
    (state) => state.polygonRoiReducer.roiListWithCvapps
  );
  const dispatch = useDispatch();
  const { setFieldValue, values } = useFormikContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState(null);
  const [cannotDelete, setCannotDelete] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [floorNameError, setFloorNameError] = useState("");
  const { siteTagList } = useSelector((state) => state?.sitesReducer);

  const handleFloorNameChange = (e) => {
    const rawValue = e.target.value;
    setFieldValue(`files.${index}.floorName`, rawValue);
    setFieldValue(`files.${index}.name`, rawValue);
    setFieldValue(`floorNames.${index}`, rawValue);
    setFieldValue(`sites.${index}.floorName`, rawValue);

    // Validate format - check raw value to show error for invalid characters
    const hasError = rawValue && !validateNameField(rawValue);
    if (hasError) {
      setFloorNameError(NAMING_VALIDATION_ERROR);
    } else {
      setFloorNameError("");
    }
    onValidationChange?.(hasError);
  };

  const handleDeleteClick = (floorId) => {
    const isFloorTagged = cvAppRoisWithCVApps.some(
      (d) => Array.isArray(d.tagids) && d.tagids.includes(floorId)
    );
    const areaObjs = siteTagList.areas.filter(
      (area) => Array.isArray(area.tagids) && area.tagids.includes(floorId)
    );
    const areaIds = areaObjs.map((a) => a.id);
    const isAreaTagged = cvAppRoisWithCVApps.some(
      (roi) =>
        Array.isArray(roi.tagids) &&
        roi.tagids.some((tag) => areaIds.includes(tag))
    );

    const subAreaObjs = siteTagList.subareas.filter(
      (sub) =>
        Array.isArray(sub.tagids) &&
        sub.tagids.some((tag) => areaIds.includes(tag))
    );
    const subAreaIds = subAreaObjs.map((sa) => sa.id);
    const isSubAreaTagged = cvAppRoisWithCVApps.some(
      (roi) =>
        Array.isArray(roi.tagids) &&
        roi.tagids.some((tag) => subAreaIds.includes(tag))
    );
    if (isFloorTagged || isAreaTagged || isSubAreaTagged) {
      setShowBlockedModal(true);
      return;
    }

    if (mode === "edit" && values.files.length === 1) {
      setCannotDelete(true);
    } else {
      setCannotDelete(false);
      setSelectedFloorPlanId(floorId);
    }
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    handleDelete(selectedFloorPlanId);
    setShowDeleteModal(false);
  };

  const showPermissionsBanner = () => {
      const notificationInfo = {
        type: "No Permission",
        priority: "critical",
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    };

  return (
    <>
      <IndividualFPFrame>
        <FloorPlanHeader>
          <FloorPlanLabel>Floor plan name</FloorPlanLabel>
          {isEditable && (
            <IconButton
              data-testid="floorplanTileEdit_trashIcon"
              onClick={() =>{
                 if (!hasPermission("Site management", "Edit")) {
                      showPermissionsBanner();
                    } else {
                      handleDeleteClick(file.id);
                    }}
              }
            >
              <Icon name="trash" size="large" color="#000000" />
            </IconButton>
          )}
        </FloorPlanHeader>
        <Field name={`files.${index}.name`}>
          {({ field }) => (
            <>
              <Input
                {...field}
                type="text"
                readOnly={reviewPage}
                required={true}
                className={`${styles.formInput} ${
                  mode === "edit" && !isEditable ? styles.disabledField : null
                }`}
                disabled={mode === "edit" && !isEditable && !reviewPage}
                error={!!floorNameError}
                errorText={floorNameError || " "}
                value={
                  values.files[index].floorName.indexOf(".") > -1
                    ? values.files[index].floorName.split(".")[0]
                    : values.files[index].floorName
                }
                data-testid="floorplanTileEdit_floorplanNameInput"
                onChange={handleFloorNameChange}
              />
              <ErrorMessage
                name={`files.${index}.floorName`}
                component={"div"}
                className={styles.errorLabel}
                style={{ marginLeft: "10px" }}
              />
            </>
          )}
        </Field>
        <FrameForImages style={{ backgroundImage: `url(${file?.url})` }} />
        <UploadedImageNameEdit>
          {isEditable && (
            <FileUploader
              multiple={false}
              handleChange={(newFile) => handleFileChange(newFile, index)}
              name="files"
              onSizeError={sizeErrorFunction}
              types={fileTypes}
              maxSize={maxSize}
              fileOrFiles={values.files}
            >
              <StyledButton>Change image</StyledButton>
            </FileUploader>
          )}
        </UploadedImageNameEdit>
      </IndividualFPFrame>

      {showBlockedModal && (
        <Modal
          opened={showBlockedModal}
          onOpenedChange={(open) => setShowBlockedModal(open)}
        >
          <ModalTitle>
            <Title size="large" bold>
              Delete floor
            </Title>
          </ModalTitle>
          <br />
          <br />
          <Notification
            type="error"
            hideCloseButton={true}
            title="Cannot delete floor"
            subtitle="There are currently apps subscribed to on this floor. Please delete all active regions of interest and unsubscribe from apps in order to delete this floor."
          />
        </Modal>
      )}

      {showDeleteModal && (
        <Modal opened={showDeleteModal} onOpenedChange={setShowDeleteModal}>
          {cannotDelete ? (
            <>
              <ModalTitle>
                <Title size="large" bold>
                  Delete floor
                </Title>
              </ModalTitle>
              <br />
              <br />
              <Notification
                type="error"
                hideCloseButton={true}
                title="Must have at least one floor plan"
                subtitle="A site must have at least one floor plan at all times"
              />
            </>
          ) : (
            <>
              <ModalTitle>
                <Title size="large" bold>
                  Delete floor
                </Title>
              </ModalTitle>
              <ModalBody>
                <Notification
                  type="warning"
                  hideCloseButton={true}
                  title="Deleting a floor"
                  subtitle="Deleting a floor plan will delete this floor, including an areas or sub-areas that it contains, from the portal"
                />
                <br />
                Are you sure you want to delete this floor plan?
              </ModalBody>
              <ModalFooter>
                <ButtonGroup
                  childWidth={"100%"}
                  viewport={"desktop"}
                  rowQuantity={{ desktop: 2 }}
                  data={[
                    {
                      children: "Cancel",
                      size: "medium",
                      use: "secondary",
                      "data-testid": "floorplanTileEdit_cancelButton",
                      onClick: () => setShowDeleteModal(false),
                    },
                    {
                      children: "Delete",
                      size: "medium",
                      use: "primary",
                      "data-testid": "floorplanTileEdit_deleteButton",
                      onClick: confirmDelete,
                    },
                  ]}
                  alignment={"left"}
                />
              </ModalFooter>
            </>
          )}
        </Modal>
      )}
    </>
  );
}

export default FloorPlanTileEdit;
